import {
  BLUR_EFFECTS,
  POSTPROCESSING_EFFECTS,
  VYBY_EFFECTS,
} from '@/etc/consts'
import { TRANSFORM2D_ENVELOPE_PROP } from './transform2d'

const QUICKBAR_SMART_TWO_D_TYPES = new Set([
  'video',
  'image',
  'text',
  'd1',
  'd2',
])

const QUICKBAR_SMART_THREE_D_TYPES = new Set([
  'd3',
])

export const QUICKBAR_ZOOM2D_ENVELOPE_PROP = 'zoom2d'
export const QUICKBAR_ZOOM3D_ENVELOPE_PROP = 'zoom3d'
export const QUICKBAR_SMART_OFF_ENVELOPE_PROP = 'off'
const EFFECT_ENVELOPE_PREFIX = 'effect.'

function humanizeSmartTargetPart(value) {
  return `${value || ''}`
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

function getEffectSchema(effect) {
  const code = `${effect?.code || ''}`.trim().toLowerCase()
  if (!code) return null
  if (code === 'postprocessing') {
    const math = `${effect?.math || ''}`.trim()
    return POSTPROCESSING_EFFECTS?.[math] || null
  }
  if (code === 'blur') return BLUR_EFFECTS?.blur || null
  return null
}

function getEffectDisplayName(effect) {
  const schemaName = getEffectSchema(effect)?.name
  if (schemaName) return schemaName
  const catalogName = VYBY_EFFECTS?.[effect?.code]?.name
  if (catalogName) return catalogName
  return humanizeSmartTargetPart(effect?.math || effect?.code || 'effect')
}

function getEffectParamLabel(effect, param) {
  const schemaLabel = getEffectSchema(effect)?.controls?.[param]?.name
  if (schemaLabel) return schemaLabel
  return humanizeSmartTargetPart(param)
}

function getAdjustmentEffectSmartTargets(strip, asset) {
  const type = `${asset?.type || ''}`.trim().toLowerCase()
  if (type !== 'adjustment') return []
  const effects = strip?.effects || {}
  const rawTargets = []

  Object.keys(effects).forEach((eid) => {
    const effect = effects[eid]
    const auto = effect?.controls?.auto || {}
    Object.keys(auto).forEach((param) => {
      if (auto[param] !== 'smart') return
      rawTargets.push({
        prop: `${EFFECT_ENVELOPE_PREFIX}${eid}.${param}`,
        label: getEffectParamLabel(effect, param),
        effectName: getEffectDisplayName(effect),
      })
    })
  })

  const duplicateCounts = rawTargets.reduce((acc, target) => {
    acc[target.label] = (acc[target.label] || 0) + 1
    return acc
  }, {})

  return rawTargets.map((target) => ({
    prop: target.prop,
    label: duplicateCounts[target.label] > 1
      ? `${target.effectName}: ${target.label}`
      : target.label,
    kind: 'effect',
  }))
}

export function isQuickbarZoomEnvelopeProp(prop) {
  return prop === QUICKBAR_ZOOM2D_ENVELOPE_PROP || prop === QUICKBAR_ZOOM3D_ENVELOPE_PROP
}

export function getQuickbarZoomCanonicalProp(prop) {
  if (!isQuickbarZoomEnvelopeProp(prop)) return null
  return 'scale.x'
}

export function getQuickbarZoomLinkedProps(prop) {
  if (prop === QUICKBAR_ZOOM2D_ENVELOPE_PROP) {
    return ['scale.x', 'scale.y']
  }
  if (prop === QUICKBAR_ZOOM3D_ENVELOPE_PROP) {
    return ['scale.x', 'scale.y', 'scale.z']
  }
  return []
}

export function getQuickbarSmartLabel(prop) {
  if (prop === TRANSFORM2D_ENVELOPE_PROP) return 'Animate'
  if (prop === 'opacity') return 'Opacity'
  if (prop === QUICKBAR_ZOOM2D_ENVELOPE_PROP) return '2D Zoom'
  if (prop === QUICKBAR_ZOOM3D_ENVELOPE_PROP) return '3D Zoom'
  if (prop === QUICKBAR_SMART_OFF_ENVELOPE_PROP) return 'Off'
  return String(prop || '')
}

export function isQuickbarSmartOffProp(prop) {
  return prop === QUICKBAR_SMART_OFF_ENVELOPE_PROP
}

export function isQuickbarSmartEnvelopeProp(prop) {
  return prop === 'opacity' || prop === TRANSFORM2D_ENVELOPE_PROP || isQuickbarZoomEnvelopeProp(prop)
}

export function getQuickbarSmartCandidates(asset) {
  const type = `${asset?.type || ''}`.trim().toLowerCase()
  const defs = asset?.defaultProps || {}
  const ret = []

  if (!type || type === 'audio' || type === 'adjustment' || type === 'scene' || type === 'camera') {
    return ret
  }

  if (
    type === 'text' &&
    typeof defs['position.x'] === 'number' &&
    typeof defs['position.y'] === 'number' &&
    typeof defs['scale.x'] === 'number' &&
    typeof defs['scale.y'] === 'number' &&
    typeof defs['rotation.z'] === 'number'
  ) {
    ret.push(TRANSFORM2D_ENVELOPE_PROP)
  }

  if (typeof defs.opacity === 'number') ret.push('opacity')

  if (
    QUICKBAR_SMART_TWO_D_TYPES.has(type) &&
    typeof defs['scale.x'] === 'number' &&
    typeof defs['scale.y'] === 'number'
  ) {
    ret.push(QUICKBAR_ZOOM2D_ENVELOPE_PROP)
  }

  if (
    QUICKBAR_SMART_THREE_D_TYPES.has(type) &&
    typeof defs['scale.x'] === 'number' &&
    typeof defs['scale.y'] === 'number' &&
    typeof defs['scale.z'] === 'number'
  ) {
    ret.push(QUICKBAR_ZOOM3D_ENVELOPE_PROP)
  }

  if (ret.length) ret.push(QUICKBAR_SMART_OFF_ENVELOPE_PROP)

  return ret
}

export function getStripQuickbarSmartTargets(strip, asset) {
  const type = `${asset?.type || ''}`.trim().toLowerCase()
  if (type === 'adjustment') {
    return [
      { prop: QUICKBAR_SMART_OFF_ENVELOPE_PROP, label: getQuickbarSmartLabel(QUICKBAR_SMART_OFF_ENVELOPE_PROP), kind: 'strip' },
      ...getAdjustmentEffectSmartTargets(strip, asset),
    ]
  }
  return getQuickbarSmartCandidates(asset).map((prop) => ({
    prop,
    label: getQuickbarSmartLabel(prop),
    kind: 'strip',
  }))
}

export function getStripQuickbarSmartCandidates(strip, asset) {
  return getStripQuickbarSmartTargets(strip, asset).map((target) => target.prop)
}

export function getStripQuickbarSmartLabel(strip, asset, prop) {
  const match = getStripQuickbarSmartTargets(strip, asset).find((target) => target.prop === prop)
  if (match) return match.label
  if (`${prop || ''}`.startsWith(EFFECT_ENVELOPE_PREFIX)) {
    const parts = `${prop}`.split('.')
    return humanizeSmartTargetPart(parts.slice(2).join('.'))
  }
  return getQuickbarSmartLabel(prop)
}

export function parseEffectEnvelopeProp(prop) {
  const raw = `${prop || ''}`.trim()
  if (!raw.startsWith(EFFECT_ENVELOPE_PREFIX)) return null
  const [, euuid, ...paramParts] = raw.split('.')
  const param = paramParts.join('.')
  if (!euuid || !param) return null
  return { euuid, param }
}

export function supportsQuickbarSmartProp(asset, prop) {
  return getQuickbarSmartCandidates(asset).includes(prop)
}

export function getDefaultQuickbarSmartProp(asset) {
  const type = `${asset?.type || ''}`.trim().toLowerCase()
  const candidates = getQuickbarSmartCandidates(asset)
  if (type === 'text' && candidates.includes(TRANSFORM2D_ENVELOPE_PROP)) {
    return TRANSFORM2D_ENVELOPE_PROP
  }
  if (candidates.includes(QUICKBAR_SMART_OFF_ENVELOPE_PROP)) {
    return QUICKBAR_SMART_OFF_ENVELOPE_PROP
  }
  if (candidates.includes(QUICKBAR_ZOOM2D_ENVELOPE_PROP)) return QUICKBAR_ZOOM2D_ENVELOPE_PROP
  if (candidates.includes(QUICKBAR_ZOOM3D_ENVELOPE_PROP)) return QUICKBAR_ZOOM3D_ENVELOPE_PROP
  return candidates[0] || null
}

export function getDefaultQuickbarSmartPropForStrip(strip, asset) {
  const type = `${asset?.type || ''}`.trim().toLowerCase()
  if (type === 'adjustment') {
    return QUICKBAR_SMART_OFF_ENVELOPE_PROP
  }
  return getDefaultQuickbarSmartProp(asset)
}

export function normalizeQuickbarSmartProp(asset, prop) {
  if (isQuickbarSmartOffProp(prop) && getQuickbarSmartCandidates(asset).includes(prop)) return prop
  if (supportsQuickbarSmartProp(asset, prop)) return prop
  return getDefaultQuickbarSmartProp(asset)
}

export function normalizeQuickbarSmartPropForStrip(strip, asset, prop) {
  const candidates = getStripQuickbarSmartCandidates(strip, asset)
  if (candidates.includes(prop)) return prop
  return getDefaultQuickbarSmartPropForStrip(strip, asset)
}

function readBaselineValue({ strip, asset, prop }) {
  const canonicalProp = isQuickbarZoomEnvelopeProp(prop)
    ? getQuickbarZoomCanonicalProp(prop)
    : prop
  const entryProps = strip?.animation?.keyframes?.[0]?.props || {}
  const defs = asset?.defaultProps || {}

  if (typeof entryProps?.[canonicalProp] === 'number') return entryProps[canonicalProp]
  if (typeof defs?.[canonicalProp] === 'number') return defs[canonicalProp]
  if (canonicalProp === 'opacity') return 1
  return 1
}

export function buildDefaultQuickbarSmartGroup({ strip, asset, prop }) {
  const value = readBaselineValue({ strip, asset, prop })
  return {
    start: { value },
    left: { value },
    right: { value },
    end: { value },
    offsets: { entry: 0, exit: 0 },
    links: { rightToLeft: false },
    sustainEasing: 'Linear.None',
    entryEasing: 'Linear.None',
    exitEasing: 'Linear.None',
  }
}
